import { Locale, defaultLocale, includeLocaleInUrl } from "../../lib/i18n"
import { useLocaleContext } from "../i18n/context"

/**
 * Ensures a path has the correct locale prefix according to localization rules
 * @param path - The original path to be processed
 * @param locale - The locale to use for the path
 * @returns The path with proper locale prefix if needed
 */
export const ensureLocalizedPath = (path: string, locale: Locale): string => {
  // Don't modify external links or special links
  if (
    path.startsWith("http") ||
    path.startsWith("#") ||
    path.startsWith("mailto:") ||
    path.startsWith("tel:")
  ) {
    return path
  }

  // Add locale prefix to internal links if needed
  if (includeLocaleInUrl(locale) && !path.startsWith(`/${locale}/`)) {
    return `/${locale}${path.startsWith("/") ? path : `/${path}`}`
  }

  return path
}

export const buildLocalizedPath = (locale: string, segments: string[]) => {
  return locale === defaultLocale
    ? `/${segments.join("/")}`
    : `/${locale}/${segments.join("/")}`
}

/**
 * Hook to access the current locale and change it
 * @returns Object with locale and changeLocale function
 */
export const useLocale = () => {
  const { locale } = useLocaleContext()

  const changeLocale = (newLocale: string) => {
    window.location.href = newLocale === defaultLocale ? "/" : `/${newLocale}`
  }

  return {
    locale,
    changeLocale,
  }
}
